import { useEffect, useRef } from 'react';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GLOBAL_NAVIGATION_HEIGHT, PAGE_LAYOUT_FRAME_HEIGHT } from '@/shared/lib/constants';
import { Banner } from '../../banner/components/Banner';
import { Text } from '../../text';
import { type FieldLabels, flattenErrors, scrollToField } from './helpers';

/**
 * fixed/stickyヘッダーを考慮したスクロールマージン
 * GlobalNavigation (56px) + PageLayoutFrame (48px) + 余白 (16px)
 */
const SCROLL_MARGIN_TOP = GLOBAL_NAVIGATION_HEIGHT + PAGE_LAYOUT_FRAME_HEIGHT + 16;

type FormErrorSummaryProps<T extends FieldValues> = {
  /**
   * react-hook-formのエラーオブジェクト
   */
  errors: FieldErrors<T>;
  /**
   * フォーム送信済みフラグ（送信前はエラー表示しない）
   */
  isSubmitted: boolean;
  /**
   * フォーム送信回数（送信のたびにスクロールするため）
   */
  submitCount: number;
  /**
   * フィールドキーとラベルのマッピング
   * 型安全: フォームの必須フィールドに対応するラベルが必須
   * （オプショナルフィールドのラベルは任意）
   * 例: { project_name: '事業名', year: '事業年度' }
   */
  fieldLabels: FieldLabels<T>;
  /**
   * エラー表示時に自動スクロールするか
   * @default true
   */
  scrollOnError?: boolean;
  /**
   * カスタムクラス
   */
  className?: string;
};

// 型をre-export
export type { FieldLabels } from './helpers';

/**
 * フォームエラーサマリーコンポーネント
 *
 * フォームのバリデーションエラーを一覧表示するコンポーネント。
 * GOV.UK Error Summaryパターンを参考に設計。
 *
 * Figmaデザイン準拠:
 * https://www.figma.com/design/K9BryoBh0t69xPihm3VBho/%E3%80%90DnR%E3%80%91DesignSystem?node-id=4788-14488
 *
 */
export const FormErrorSummary = <T extends FieldValues>({
  errors,
  isSubmitted,
  submitCount,
  fieldLabels,
  scrollOnError = true,
  className,
}: FormErrorSummaryProps<T>) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const prevSubmitCountRef = useRef(0);

  // エラーをフラット化
  const fieldErrors = flattenErrors(errors, fieldLabels);
  const errorCount = fieldErrors.length;
  const hasErrors = isSubmitted && errorCount > 0;

  // 送信のたびにエラーがあればスクロール
  useEffect(() => {
    if (scrollOnError && hasErrors && submitCount > prevSubmitCountRef.current) {
      // レンダリング完了後にスクロール実行
      requestAnimationFrame(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    prevSubmitCountRef.current = submitCount;
  }, [hasErrors, scrollOnError, submitCount]);

  // 送信前またはエラーがない場合は表示しない
  if (!hasErrors) {
    return null;
  }

  return (
    <div ref={containerRef} style={{ scrollMarginTop: `${SCROLL_MARGIN_TOP}px` }}>
      <Banner
        type="error"
        title={t('form.error-summary.title', { total: errorCount })}
        description={
          <ul className="mt-1 flex list-disc flex-col gap-0.5 pl-5 text-sm">
            {fieldErrors.map((error) => (
              <li key={error.key}>
                <Text variant="link" color="error" onClick={() => scrollToField(error.key)}>
                  {error.label}: {error.message}
                </Text>
              </li>
            ))}
          </ul>
        }
        className={className}
      />
    </div>
  );
};
